import { useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useDocumentState } from '../../../../../../contexts/DocumentContext';
import { documentStateFromResponse } from '../../../../../../utils/document-wizard-utils/document-state-from-response';
import PropdocsModal from '../../../../../modal/PropdocsModal';
import { approveDocumentReview, rejectDocumentReview } from '../../../../../../api/mutations';
import { mutations } from '../../../../../../api';
import { flushActiveEditor } from '../../../../document-editor/flushActiveEditor';
import { getDocumentById } from '../../../../../../api/queries_v2';
import { useNotifications } from '../../../../../../notifications';
import { useIsMobile } from '../../../../../../shared/hooks/useIsMobile';
import { OptionalMessageInputRef } from '../../../../document-wizard-modal/custom-modals/document-wizard-action-modal/OptionalMessageInputRef';
import {
  ApprovalRequestActions,
  rejectApprovalSuccessMessage,
} from './RequestInternalApprovalModalConstants';

interface Props {
  onHide: () => void;
  show: boolean;
  documentId: string;
  requestApprovalAction: ApprovalRequestActions | null;
  dealId: string;
  teamId: string;
  reviewId: string;
  handleSaveDocument?: () => Promise<void>;
}

const HandleInternalApprovalActionsModal = ({
  onHide,
  show,
  documentId,
  requestApprovalAction,
  dealId,
  reviewId,
  teamId,
  handleSaveDocument,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { updateDocumentState } = useDocumentState();
  const additionalMessageInputRef = useRef<HTMLTextAreaElement>(null);
  const approveModal = requestApprovalAction === ApprovalRequestActions.APPROVE;
  const isMobile = useIsMobile();
  const apolloClient = useApolloClient();
  const [rejectDocumentReviewMutation] = useMutation(rejectDocumentReview, {
    refetchQueries: [
      {
        query: getDocumentById,
        variables: {
          documentId,
        },
      },
    ],
    awaitRefetchQueries: true,
  });
  // No refetchQueries on approve — the immediate redirect refetches anyway.
  // This eliminates race condition where autosave triggers after approval status changes
  const [approveDocumentReviewMutation] = useMutation(approveDocumentReview);
  // Polymorphic version snapshot — the docx-native equivalent of CKEditor's
  // RevisionTracker.saveRevision().
  const [saveVersionMutation] = useMutation(mutations.saveDocumentVersion);

  // Query to get updated document data (including approval request status)
  useQuery(getDocumentById, {
    variables: { documentId },
    skip: !documentId,
  });

  const [, { error: notifyError, success: notifySuccess }] = useNotifications();

  const handleApprove = useCallback(async () => {
    setIsLoading(true);

    const additionalMessage = additionalMessageInputRef.current?.value;

    try {
      // Save any pending edits BEFORE approving (prevents "document cannot be edited" error)
      if (handleSaveDocument) {
        try {
          await handleSaveDocument();
        } catch (saveError) {
          // Still proceed with approval even if save fails - approval is more important
        }
      }

      // The approval revision must exist before the mutation, or the mutation's cache write races the revision read.
      // Check if this approval will complete the review process (final approval)
      try {
        const { getDocumentById: currentDocument } = apolloClient.readQuery({
          query: getDocumentById,
          variables: { documentId },
        });
        const approvalRequest = currentDocument?.approvalRequest;

        if (approvalRequest) {
          const reviews = approvalRequest.reviews || [];

          // Find reviews that are not yet approved or rejected
          const pendingReviews = reviews.filter(
            (review: any) => !review.approvedAt && !review.rejectedAt
          );

          // This is the final approval if only 1 review is pending (the current one)
          const isFinalApproval = pendingReviews.length === 1;

          if (isFinalApproval) {
            // FIX RACE CONDITION: flush pending changes FIRST so the autosave
            // lands before the approval snapshot and the ids order correctly.
            await flushActiveEditor(documentId);

            // The backend commits the autosave revision asynchronously; without this delay the next read can miss it.
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Get names of already approved reviewers
            const approvedReviews = reviews.filter((review: any) => review.approvedAt);
            const alreadyApprovedNames = approvedReviews
              .map((review: any) => review.reviewer?.fullName)
              .filter(Boolean);

            // Add current reviewer's name (the one approving now)
            const currentReviewer = reviews.find((review: any) => review.id === reviewId);
            const currentReviewerName = currentReviewer?.reviewer?.fullName;

            const allApproverNames = currentReviewerName
              ? [...alreadyApprovedNames, currentReviewerName]
              : alreadyApprovedNames;

            // Build revision name
            let revisionName = 'Approved Version'; // Default fallback
            if (allApproverNames.length === 1) {
              revisionName = `Approved by ${allApproverNames[0]}`;
            } else if (allApproverNames.length > 1) {
              const lastApprover = allApproverNames.pop();
              revisionName = `Approved by ${allApproverNames.join(', ')} and ${lastApprover}`;
            }

            // NOW create the approval revision (will have higher ID than autosave)
            await saveVersionMutation({
              variables: {
                documentId: String(documentId),
                documentType: 'Document',
                name: revisionName,
                event: 'approval',
              },
            });
          }
        }
      } catch (error) {
        // Don't block the approval process if revision creation fails
      }

      await approveDocumentReviewMutation({
        variables: {
          reviewId,
          reason: additionalMessage,
        },
      });

      // Redirect immediately — staying mounted lets a queued autosave overwrite the approved body.
      // Redirect happens immediately after approval mutation completes (no refetch delay)
      // This prevents autosave from triggering after approval status changes to "approved"
      // Full page reload will fetch fresh data and show updated approval status
      window.location.href = `/teams/${teamId}/deals/${dealId}`;
    } catch (error) {
      notifyError({
        message: error instanceof Error ? error.message : 'Failed to approve document review'
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    approveDocumentReviewMutation,
    notifyError,
    reviewId,
    notifySuccess,
    formatMessage,
    navigate,
    teamId,
    dealId,
    handleSaveDocument,
    apolloClient,
    documentId,
  ]);

  const handleReject = useCallback(async () => {
    setIsLoading(true);
    const additionalMessage = additionalMessageInputRef.current?.value;

    try {
      await rejectDocumentReviewMutation({
        variables: {
          reviewId,
          reason: additionalMessage,
        },
      });

      // Rejection changes state the context caches separately from Apollo; sync both or the header renders the stale status.
      // DocumentEditor reads from context state, so we need to update the context
      // The refetchQueries above updates Apollo cache, but context also needs updating
      try {
        const cachedData = apolloClient.readQuery({
          query: getDocumentById,
          variables: { documentId },
        });

        if (cachedData?.getDocumentById) {
          // Convert response to context state and update
          updateDocumentState(documentStateFromResponse(cachedData.getDocumentById as any, false));
        }
      } catch (cacheError) {
        // Cache read failed - modal will close, user may need to refresh
        console.warn('Failed to read updated document from cache:', cacheError);
      }

      notifySuccess({
        message: formatMessage(rejectApprovalSuccessMessage),
      });

      onHide();
    } catch (error) {
      notifyError({
        message: error instanceof Error ? error.message : 'Failed to reject document review'
      });
    } finally {
      setIsLoading(false);
    }
  }, [rejectDocumentReviewMutation, notifyError, reviewId, notifySuccess, formatMessage, onHide, apolloClient, documentId, updateDocumentState]);

  return (
    <PropdocsModal
      size={isMobile ? 'sm' : 'lg'}
      show={show}
      setShow={onHide}
      className={isMobile ? 'mobile-approval-modal' : ''}
      title={
        <FormattedMessage
          id="document-wizard-modal.handle_internal_approval"
          defaultMessage={`${approveModal ? ApprovalRequestActions.APPROVE : ApprovalRequestActions.REJECT} Request`}
        />
      }
    >
      <>
        <div
          style={{
            marginBottom: '1.5rem',
            fontSize: isMobile ? '0.9rem' : '1rem',
            lineHeight: isMobile ? '1.4' : '1.5',
          }}
        >
          {approveModal
            ? 'Document will remain read-only after approval'
            : 'This will reject the entire approval request'}
        </div>
        <OptionalMessageInputRef ref={additionalMessageInputRef} />

        <div
          style={{
            display: 'flex',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
            flexDirection: 'row', // Always row, even on mobile
            gap: isMobile ? '0.5rem' : '1rem',
            paddingTop: '1.5rem',
          }}
        >
          <button
            type="button"
            className={`btn btn-outline-secondary ${isMobile ? 'btn-lg' : ''}`}
            onClick={onHide}
            style={isMobile ? { minHeight: '48px', flex: '1' } : {}}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${approveModal ? 'btn-success' : 'btn-danger'} ${isMobile ? 'btn-lg' : ''}`}
            onClick={approveModal ? handleApprove : handleReject}
            disabled={isLoading}
            style={isMobile ? { minHeight: '48px', flex: '1' } : {}}
          >
            {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
            {approveModal ? ApprovalRequestActions.APPROVE : ApprovalRequestActions.REJECT}
          </button>
        </div>
      </>
    </PropdocsModal>
  );
};

export default HandleInternalApprovalActionsModal;
